import { KaboodleToaster } from "components/common";
import CreateClaimModal from "components/Modals/CreateClaimModal";
import ResolveCMSErrorModal from "components/Modals/ResolveCMSErrorModal";
import { SECTION } from "constants/SettingsConstants";
import { useEffect, useMemo } from "react";
import { useParams } from "react-router-dom";
import {
  setShowResolveCMSErrorModal,
  setShowArchiveModal,
} from "reducers/claims";
import {
  setErrorDrawerVisible,
  setWarningDrawerVisible,
} from "reducers/drawer";
import { setCurrentSection, setShowAddModal } from "reducers/main";
import { useResolveClaimCmsErrorMutation } from "services/claims-api.ts";
import { useDispatch, useSelector } from "utils/redux";
import ArchiveClaimModal from "components/Modals/ArchiveClaimModal";
import ClaimDocuments from "./ClaimDocumentsDrawerTable";

type ClaimsPageContainerPropTypes = {
  header?: React.ReactNode;
  children?: React.ReactNode;
  footer?: React.ReactNode;
};

const ClaimsPageContainer = ({
  header,
  footer,
  children,
}: ClaimsPageContainerPropTypes) => {
  const dispatch = useDispatch();
  const { claimId } = useParams<{ claimId: string }>();
  const showAddModal = useSelector((state) => state.main.showAddModal);
  const showArchiveModal = useSelector((state) => state.claim.showArchiveModal);

  useEffect(() => {
    dispatch(setCurrentSection(SECTION.SECTION_111_CLAIMS));
  }, [dispatch]);

  const showResolveCMSErrorModal = useSelector(
    (state) => state.claim.showResolveCMSErrorModal,
  );

  const [resolveClaimCmsError] = useResolveClaimCmsErrorMutation();
  const handleResolveCMSError = async () => {
    try {
      await resolveClaimCmsError({
        claimId: parseInt(claimId, 10),
        data: {},
      }).unwrap();
      dispatch(setShowResolveCMSErrorModal(false));
      KaboodleToaster.success("Success.");
    } catch (error) {
      KaboodleToaster.error("Failed to mark CMS errors as resolved.");
    }
  };

  const resolveCMSErrorModal = useMemo(() => {
    return (
      <ResolveCMSErrorModal
        isOpen={showResolveCMSErrorModal}
        onClose={() => {
          dispatch(setShowResolveCMSErrorModal(false));
        }}
        onResolve={handleResolveCMSError}
      />
    );
  }, [dispatch, showResolveCMSErrorModal]);

  const createClaimModal = useMemo(() => {
    return (
      <CreateClaimModal
        isOpen={showAddModal}
        onClose={() => {
          dispatch(setShowAddModal(false));
        }}
      />
    );
  }, [dispatch, showAddModal]);

  const claimDocumentsDrawer = useMemo(() => {
    if (!claimId) {
      return null;
    }
    return <ClaimDocuments claimId={Number(claimId)} />;
  }, [dispatch, claimId]);

  const archiveClaimModal = useMemo(() => {
    if (!claimId || !showArchiveModal) {
      return null;
    }
    return (
      <ArchiveClaimModal
        claimId={Number(claimId)}
        isOpen={showArchiveModal}
        onClose={() => dispatch(setShowArchiveModal(false))}
      />
    );
  }, [dispatch, claimId, showArchiveModal]);

  const { validationWarnings, validationErrors } = useSelector(
    (state) => state.main,
  );

  useEffect(() => {
    if (validationErrors?.length === 0) {
      dispatch(setErrorDrawerVisible(false));
    }
  }, [validationErrors, dispatch]);

  useEffect(() => {
    if (validationWarnings?.length === 0) {
      dispatch(setWarningDrawerVisible(false));
    }
  }, [validationWarnings, dispatch]);

  return (
    <>
      {archiveClaimModal}
      {createClaimModal}
      {resolveCMSErrorModal}
      {claimDocumentsDrawer}
      <div className="ClaimsPageContainer">
        {header && <div className="ClaimsPageContainer__Header">{header}</div>}
        <div className="ClaimsPageContainer__Forms">{children}</div>
        {footer && <div className="ClaimsPageContainer__Footer">{footer}</div>}
      </div>
    </>
  );
};

export default ClaimsPageContainer;
